import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { WorkflowTemplate, WorkflowPhase, WorkflowStep } from '../types/WorkflowTypes';
import DetailModal from './DetailModal';
import './PipelineView.css';

interface PipelineViewProps {
  workflow: WorkflowTemplate | null;
  expandedPhases: Record<string, boolean>;
  togglePhase: (phaseName: string) => void;
  showDetailedSteps: boolean;
}

const PipelineView: React.FC<PipelineViewProps> = ({ 
  workflow, 
  expandedPhases, 
  togglePhase, 
  showDetailedSteps 
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredElement, setHoveredElement] = useState<{ type: string; data: any; x: number; y: number } | null>(null);
  
  // State for the modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalElementType, setModalElementType] = useState<'phase' | 'step' | null>(null);
  const [modalElementData, setModalElementData] = useState<WorkflowPhase | WorkflowStep | null>(null);

  const showTooltip = useCallback((type: string, data: any, event: any) => {
    const { clientX, clientY } = event;
    setHoveredElement({
      type,
      data,
      x: clientX,
      y: clientY
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setHoveredElement(null);
  }, []);

  // Handle click events - switched from dblclick to click
  const handleClick = useCallback((type: 'phase' | 'step', data: WorkflowPhase | WorkflowStep) => {
    setModalElementType(type);
    setModalElementData(data);
    setModalOpen(true);
  }, []);

  // Function to wrap text in multiple lines
  const wrapText = (text: d3.Selection<SVGTextElement, unknown, null, undefined>, maxLineLength: number) => {
    const words = text.text().split(/\s+/).reverse();
    const lineHeight = 1.1; // ems
    const y = text.attr('y');
    const dy = parseFloat(text.attr('dy') || '0');
    
    let line: string[] = [];
    let lineNumber = 0;
    let tspan = text.text(null).append('tspan').attr('x', 0).attr('y', y).attr('dy', dy + 'em');
    
    let word = words.pop();
    while (word) {
      line.push(word);
      const lineText = line.join(' ');
      
      if (lineText.length > maxLineLength) {
        // If exceeding max length and not the first word, pop it back
        if (line.length > 1) {
          line.pop();
          words.push(word);
        }
        
        tspan.text(line.join(' '));
        line = [];
        lineNumber += 1;
        tspan = text.append('tspan').attr('x', 0).attr('y', y).attr('dy', lineNumber * lineHeight + dy + 'em');
      }
      
      word = words.pop();
    }
    
    // Last line
    if (line.length > 0) {
      tspan.text(line.join(' '));
    }
  };

  const renderSteps = useCallback((
    phase: WorkflowPhase, 
    phaseGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    phaseX: number,
    phaseY: number,
    stepRadius: number,
    stepSpacing: number
  ) => {
    const stepsGroup = phaseGroup.append('g')
      .attr('class', 'steps-group');

    // Horizontal line connecting steps
    stepsGroup.append('line')
      .attr('x1', stepRadius + 5)
      .attr('y1', 0)
      .attr('x2', (phase.steps.length * stepSpacing) + stepRadius)
      .attr('y2', 0)
      .attr('class', 'step-connecting-line');

    // Draw steps as circles with labels
    phase.steps.forEach((step, stepIndex) => {
      const stepX = (stepIndex + 1) * stepSpacing + stepRadius;
      
      // Step group - add event listeners to the group for better hit area
      const stepGroup = stepsGroup.append('g')
        .attr('class', 'step-group')
        .attr('transform', `translate(${stepX}, 0)`)
        .style('cursor', 'pointer')
        .on('mouseover', (event) => showTooltip('step', step, event))
        .on('mouseout', hideTooltip)
        .on('click', (event) => {
          // Prevent event from propagating
          event.stopPropagation();
          // Always show step details without toggling the phase
          handleClick('step', step);
        });

      // Step circle with stronger stroke color based on status
      stepGroup.append('circle')
        .attr('r', stepRadius)
        .attr('class', `step-circle step-${step.default_status.toLowerCase()} step-type-${step.type.toLowerCase()}`);

      // Step icon based on type
      let stepIcon = '📄';
      if (step.type === 'milestone') stepIcon = '🏁';
      if (step.type === 'submission') stepIcon = '📤';
      if (step.type === 'presentation') stepIcon = '🎯';
      if (step.type === 'contract_preparation') stepIcon = '📝';
      if (step.type === 'signature') stepIcon = '✍️';
      if (step.type === 'offer') stepIcon = '💼';
      if (step.type === 'notification') stepIcon = '🔔';
      if (step.type === 'review') stepIcon = '👁️';
      if (step.type === 'endorsement') stepIcon = '👍';
      if (step.type === 'document_preparation') stepIcon = '📋';
      if (step.type === 'application_review') stepIcon = '🔍';
      if (step.type === 'project_setup') stepIcon = '🏗️';
      if (step.type === 'questionnaire_distribution') stepIcon = '📨';
      if (step.type === 'questionnaire_completion') stepIcon = '✅';
      if (step.type === 'contract_generation') stepIcon = '📜';
      if (step.type === 'contract_signing') stepIcon = '✒️';

      // The icon text element
      stepGroup.append('text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('class', 'step-icon')
        .text(stepIcon);

      // Create a text element for step label with automatic wrapping
      const stepLabelText = stepGroup.append('text')
        .attr('y', stepRadius + 15)
        .attr('text-anchor', 'middle')
        .attr('class', 'step-label')
        .text(step.title);
      
      // Use the wrapText function to split into multiple lines with max 18 chars per line
      wrapText(stepLabelText, 18);

      // Small type indicator - position it after the wrapped text
      const labelBBox = stepLabelText.node()?.getBoundingClientRect();
      const labelHeight = labelBBox ? (labelBBox.height + 5) : 20;
      
      stepGroup.append('text')
        .attr('y', stepRadius + 15 + labelHeight)
        .attr('text-anchor', 'middle')
        .attr('class', 'step-type')
        .text(step.type);
    });
  }, [showTooltip, hideTooltip, handleClick]);

  const renderPipeline = useCallback(() => {
    if (!workflow || !svgRef.current || !workflow.phases) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Calculate dimensions based on content
    const phaseCount = workflow.phases.length || 1;
    const maxStepCount = Math.max(...workflow.phases.map(phase => phase.steps.length), 1);
    
    // Base dimensions and spacing
    const phaseRadius = 30;
    const stepRadius = 20;
    const stepSpacing = 130; // Increased horizontal spacing between steps
    const phaseSpacing = 150; // Vertical spacing between phases
    
    // Calculate the max phase name length for padding
    const maxPhaseNameLength = Math.max(...workflow.phases.map(phase => phase.name.length), 8);
    const phaseNamePadding = Math.max(maxPhaseNameLength * 8, 100); // At least 8px per character
    
    const horizontalMargin = phaseNamePadding + 40; // Dynamic left padding based on phase names
    const verticalMargin = 50;
    
    // Calculate total height needed for all phases
    const totalHeight = phaseCount * phaseSpacing + verticalMargin * 2;
    
    // Calculate width needed for the longest phase
    const baseContentWidth = horizontalMargin * 2 + phaseRadius * 2;
    // Add space for steps in the longest phase
    const maxPhaseWidth = baseContentWidth + maxStepCount * stepSpacing + 100;
    
    // Set SVG dimensions
    const width = Math.max(dimensions.width, maxPhaseWidth);
    const height = totalHeight;
    
    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');
    
    // Title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', verticalMargin / 2)
      .attr('text-anchor', 'middle')
      .attr('class', 'pipeline-title')
      .text(workflow.name);

    // Create a container for content with horizontal scrolling
    const contentGroup = svg.append('g')
      .attr('transform', `translate(${horizontalMargin}, ${verticalMargin})`);
    
    // Draw phases as rows
    if (workflow.phases && workflow.phases.length > 0) {
      workflow.phases.forEach((phase, phaseIndex) => {
        const phaseY = phaseIndex * phaseSpacing + phaseRadius;
        const phaseX = phaseRadius;
        
        // Phase group
        const phaseGroup = contentGroup.append('g')
          .attr('class', `phase-group ${expandedPhases[phase.name] ? 'expanded' : ''}`)
          .attr('transform', `translate(${phaseX}, ${phaseY})`);
        
        // Phase circle with hover and click
        phaseGroup.append('circle')
          .attr('r', phaseRadius)
          .attr('class', `phase-circle phase-${phase.name.toLowerCase()}`)
          .style('cursor', 'pointer')
          .on('mouseover', (event) => showTooltip('phase', phase, event))
          .on('mouseout', hideTooltip)
          .on('click', (event) => {
            event.stopPropagation();
            handleClick('phase', phase);
          });

        // Phase number
        phaseGroup.append('text')
          .attr('class', 'phase-number')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .text(phaseIndex + 1);

        // Phase label to the left of the circle with dynamic padding
        phaseGroup.append('text')
          .attr('x', -phaseRadius - 15) // Less distance from circle
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'central')
          .attr('class', 'phase-label')
          .text(phase.name);

        // Toggle expand/collapse icon
        // phaseGroup.append('text')
        //   .attr('x', 0)
        //   .attr('y', -phaseRadius - 5)
        //   .attr('text-anchor', 'middle')
        //   .attr('class', 'phase-toggle material-icons')
        //   .text(expandedPhases[phase.name] ? 'expand_less' : 'expand_more')
        //   .style('cursor', 'pointer')
        //   .on('click', (event) => {
        //     event.stopPropagation();
        //     togglePhase(phase.name);
        //   });

        // Render steps if phase is expanded
        if (expandedPhases[phase.name] && showDetailedSteps) {
          renderSteps(phase, phaseGroup, phaseX, phaseY, stepRadius, stepSpacing);
        }
      });
    }

    // Add a clipping path if horizontal scrolling is needed
    if (maxPhaseWidth > dimensions.width) {
      // Add indication that content is scrollable
      svg.append('text')
        .attr('x', width - 120)
        .attr('y', height - 10)
        .attr('class', 'scroll-indicator')
        .attr('text-anchor', 'end')
        .text('← Scroll to see more →');
    }
  }, [workflow, dimensions, expandedPhases, showDetailedSteps, renderSteps, showTooltip, hideTooltip, handleClick, togglePhase]);

  // Handle dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width,
          height: 800 // Fixed height or adjust based on your needs
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Function to scroll to expanded phase (now horizontally)
  const scrollToExpandedPhase = useCallback((phaseName: string) => {
    if (!phaseName || !expandedPhases || !expandedPhases[phaseName]) return;
    
    setTimeout(() => {
      const phaseElement = document.querySelector(`.phase-group.expanded`);
      
      if (phaseElement && containerRef.current) {
        const phaseRect = phaseElement.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        
        // For vertical scrolling to make sure the phase is visible
        const scrollOffset = phaseRect.top - containerRect.top - 20;
        
        if (scrollOffset !== 0) {
          containerRef.current.scrollBy({
            top: scrollOffset,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }, [expandedPhases]);
  
  // Update the useEffect that calls renderPipeline
  useEffect(() => {
    if (workflow && workflow.phases) {
      renderPipeline();
      
      // Check if any phase is expanded and scroll to it if needed
      const expandedPhaseName = Object.keys(expandedPhases)
        .find(phaseName => expandedPhases[phaseName]);
      
      if (expandedPhaseName) {
        scrollToExpandedPhase(expandedPhaseName);
      }
    }
  }, [workflow, dimensions, renderPipeline, expandedPhases, scrollToExpandedPhase]);

  // Modal close handler
  const handleModalClose = () => {
    setModalOpen(false);
    setModalElementType(null);
    setModalElementData(null);
  };

  if (!workflow || !workflow.phases) {
    return (
      <div className="pipeline-view-container" ref={containerRef} style={{ overflowX: 'auto' }}>
        <div className="no-workflow-message">
          No workflow data available
        </div>
      </div>
    );
  }
  
  // Update styles for the container to allow horizontal scrolling
  return (
    <div className="pipeline-view-container" ref={containerRef} style={{ overflowX: 'auto' }}>
      <svg ref={svgRef}></svg>
      
      {hoveredElement && (
        <div 
          className={`tooltip ${hoveredElement.type}-tooltip`}
          style={{
            left: hoveredElement.x + 10,
            top: hoveredElement.y + 10,
          }}
        >
          <div className="tooltip-title">{hoveredElement.data.title || hoveredElement.data.name}</div>
          <div className="tooltip-type">{hoveredElement.type}: {hoveredElement.data.type || 'phase'}</div>
          {hoveredElement.data.description && (
            <div className="tooltip-description">{hoveredElement.data.description}</div>
          )}
        </div>
      )}
      
      <DetailModal 
        open={modalOpen}
        onClose={handleModalClose}
        elementType={modalElementType}
        elementData={modalElementData}
      />
    </div>
  );
};

export default PipelineView; 